<template>
	<ViewFeedItem class="view-feed-contentvideorow">
		<template v-slot:header>
			<ViewLayoutModuleHeader v-bind:title="title" v-bind:subtitle="subtitle">
				<RouterLink v-bind:to="{ name: 'PageContent' }">
					<strong>View All</strong>
				</RouterLink>
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<ViewUiStateLoader v-if="contentCollection.length === 0" />

			<div class="cards" v-if="contentCollection.length > 0">
				<ViewContentCard
					v-bind:contentModel="contentModel"
					v-bind:key="contentModel.id"
					v-for="contentModel in contentCollection.models"
					v-on:select="Handle_OnSelectContent"
				/>
			</div>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Horizontally scrolling row of video content cards. Defaults to
	 * pulling YouTube-tagged content but accepts any tag set via prop.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedContentVideoRow extends ViewBase {
		/**
		 * Subtitle copy beneath the module header
		 *
		 * @return string
		 */
		protected get subtitle(): string {
			return ''; // 'Watch and learn';
		}

		/**
		 * Title copy in the module header
		 *
		 * @return string
		 */
		protected get title(): string {
			return 'On the tape';
		}

		/**
		 * Source collection of video content
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 8 }).tags(['youtube']),
		})
		public contentCollection!: ChalkySticks.Collection.Content;

		/**
		 * Tags applied to the content collection lookup. Allows the
		 * consumer to swap to e.g. a curated channel without rewriting.
		 *
		 * @type string[]
		 */
		@Prop({
			default: () => ['youtube'],
		})
		public tags!: string[];

		/**
		 * Fetch videos on mount honoring any custom tag set passed in.
		 *
		 * @return Promise<void>
		 */
		@mounted
		public async setup(): Promise<void> {
			this.contentCollection.tags(this.tags);
			await this.contentCollection.fetch();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param ChalkySticks.Model.Content contentModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectContent(contentModel: ChalkySticks.Model.Content): Promise<void> {
			this.$router.push({
				name: 'PageContentItem',
				params: {
					id: contentModel.getSlug(),
				},
			});
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.view-feed-contentvideorow {
		.body {
			padding: 0 0 var(--padding);
		}

		.cards {
			-webkit-overflow-scrolling: touch;
			display: flex;
			gap: 0.75rem;
			overflow-x: auto;
			overflow-y: hidden;
			padding: 0 var(--padding) 0.5rem;
			scroll-snap-type: x mandatory;

			&::-webkit-scrollbar {
				display: none;
			}
		}

		.view-content-card {
			flex: 0 0 auto;
			scroll-snap-align: start;
			width: 16rem;
		}
	}
</style>
